import { CloseIcon } from '@repo/icons';
import { Button } from '@repo/ui/button';
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from '@repo/ui/drawer';
import { cn } from '@repo/utils/cn';
import { useTranslations } from 'next-intl';
import React, { useMemo, useState } from 'react';
import Deposit, { type ViewType } from '@/app/[locale]/(account)/account/deposit';
import { type QuickDepositProps } from '@/components/common/quick-deposit';
import { PAYMENT_TYPES } from '@/constants';
import { type FullMethod, usePaymentMethodsWithNetworks } from '@/hooks/usePaymentMethodsWithNetworks';
import { getActiveMethods } from '@/hooks/usePaymentMethodsWithNetworks/utils';
import { useTenant } from '@/providers/TenantProvider';

const QuickDepositDrawer: React.FC<QuickDepositProps> = ({ open, handleOpen }) => {
  const t = useTranslations(PAYMENT_TYPES.DEPOSITS);
  const {
    tenantConfig: { paymentMethods },
  } = useTenant();

  const currentMethods = paymentMethods?.deposit ?? [];
  const { hasCryptoMoonpay, hasFiat } = getActiveMethods(currentMethods) || {};

  const { creditMethods } = usePaymentMethodsWithNetworks({
    paymentType: PAYMENT_TYPES.DEPOSITS,
  });

  const [view, setView] = useState<ViewType>('CRYPTO_DETAILS');

  const availableTabs = useMemo(() => {
    const list: Array<{ key: ViewType; label: string; enabled: boolean }> = [
      {
        key: 'CRYPTO_DETAILS',
        label: t('qd-deposit-tab'),
        enabled: true,
      },
      {
        key: 'FIAT',
        label: t('qd-fiat-tab'),
        enabled: hasFiat,
      },
      {
        key: 'CREDIT_CARDS',
        label: t('qd-credit-card-tab'),
        enabled: hasCryptoMoonpay,
      },
    ];
    return list.filter((tabItem) => tabItem.enabled);
  }, [hasFiat, hasCryptoMoonpay, t]);

  const getMethod = () => {
    switch (view) {
      // case 'CRYPTO_DETAILS':
      //   return undefined;
      case 'FIAT':
        return creditMethods[0];
      case 'CREDIT_CARDS':
        return {};
      default:
        return undefined;
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpen}
    >
      <DrawerContent
        portalOverlayClassName="z-[var(--z-quick-deposit-drawer-overlay)]"
        className="z-[var(--z-quick-deposit-drawer)] min-h-[350px] border-none pb-4 outline-none vt-surface-lighter-bg"
      >
        <DrawerHeader className="flex h-[40px] w-full items-center justify-between px-3">
          <DrawerTitle>{t('qd-title')}</DrawerTitle>
          <DrawerClose asChild>
            <Button
              variant="grey"
              size="sm"
              className="rounded-full !bg-transparent !p-0"
            >
              <CloseIcon
                fill="#EFEFEF"
                className="size-3"
              />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        {availableTabs.length > 0 && (
          <div className="flex w-full items-center justify-between gap-2 p-2.5">
            {availableTabs.map(({ key, label }) => (
              <Button
                key={key}
                variant={view === key ? 'primary' : 'secondary'}
                className={cn('h-10 w-full truncate')}
                onClick={() => setView(key)}
              >
                <span className="block truncate">{label}</span>
              </Button>
            ))}
          </div>
        )}
        <Deposit
          key={view}
          isQuickDeposit
          selectedView={view}
          selectedMethod={getMethod() as FullMethod}
        />
      </DrawerContent>
    </Drawer>
  );
};

export default QuickDepositDrawer;
